import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Plus, Edit, Trash2, X, Star } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TestimonialsManager = () => {
  const { getAuthHeader } = useAuth();
  const { toast } = useToast();
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTestimonial, setEditingTestimonial] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    location: '',
    visa_type: '',
    rating: 5,
    text: '',
    date: new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
    display_order: 0,
    is_active: true
  });

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API}/admin/testimonials`,
        { headers: getAuthHeader() }
      );
      setTestimonials(response.data);
    } catch (error) {
      console.error('Failed to fetch testimonials:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingTestimonial) {
        await axios.put(
          `${API}/admin/testimonials/${editingTestimonial.id}`,
          formData,
          { headers: getAuthHeader() }
        );
        toast({ title: "Success", description: "Testimonial updated successfully" });
      } else {
        await axios.post(
          `${API}/admin/testimonials`,
          formData,
          { headers: getAuthHeader() }
        );
        toast({ title: "Success", description: "Testimonial created successfully" });
      }
      
      closeModal();
      fetchTestimonials();
    } catch (error) {
      console.error('Failed to save testimonial:', error);
      toast({
        title: "Error",
        description: "Failed to save testimonial",
        variant: "destructive"
      });
    }
  };

  const deleteTestimonial = async (id) => {
    const confirmed = await window.confirm('Are you sure you want to delete this testimonial?');
    if (!confirmed) {
      return;
    }

    try {
      await axios.delete(
        `${API}/admin/testimonials/${id}`,
        { headers: getAuthHeader() }
      );
      toast({ title: "Success", description: "Testimonial deleted successfully" });
      fetchTestimonials();
    } catch (error) {
      console.error('Failed to delete:', error);
      toast({
        title: "Error",
        description: "Failed to delete testimonial",
        variant: "destructive"
      });
    }
  };

  const openModal = (testimonial = null) => {
    if (testimonial) {
      setEditingTestimonial(testimonial);
      setFormData(testimonial);
    } else {
      setEditingTestimonial(null);
      setFormData({
        name: '',
        location: '',
        visa_type: '',
        rating: 5,
        text: '',
        date: new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
        display_order: testimonials.length + 1,
        is_active: true
      });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingTestimonial(null);
  };

  return (
    <div className="admin-page">
      <div className="page-header">
        <div>
          <h1 className="heading-1">Testimonials</h1>
          <p className="body-large">Manage client testimonials</p>
        </div>
        <button onClick={() => openModal()} className="btn-primary">
          <Plus size={20} />
          <span>Add Testimonial</span>
        </button>
      </div>

      {loading ? (
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading testimonials...</p>
        </div>
      ) : (
        <div className="testimonials-grid-admin">
          {testimonials.map(testimonial => (
            <div key={testimonial.id} className="testimonial-card-admin">
              <div className="card-header">
                <div>
                  <h3 className="heading-3">{testimonial.name}</h3>
                  <p className="caption">{testimonial.location} • {testimonial.visa_type}</p>
                </div>
                <div className="card-actions">
                  <button onClick={() => openModal(testimonial)} className="icon-btn">
                    <Edit size={18} />
                  </button>
                  <button onClick={() => deleteTestimonial(testimonial.id)} className="icon-btn delete-btn">
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
              
              <div className="rating">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <Star key={i} size={16} fill="currentColor" />
                ))}
              </div>
              
              <p className="body-small testimonial-text-preview">{testimonial.text}</p>
              
              <div className="testimonial-meta">
                <span className="caption">{testimonial.date}</span>
                <span className={`status-badge ${testimonial.is_active ? 'status-completed' : 'status-new'}`}>
                  {testimonial.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="heading-2">{editingTestimonial ? 'Edit Testimonial' : 'Add Testimonial'}</h2>
              <button onClick={closeModal} className="icon-btn">
                <X size={24} />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="modal-form">
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Name</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="form-input"
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Location</label>
                  <input
                    type="text"
                    value={formData.location}
                    onChange={(e) => setFormData({...formData, location: e.target.value})}
                    className="form-input"
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Visa Type</label>
                  <input
                    type="text"
                    value={formData.visa_type}
                    onChange={(e) => setFormData({...formData, visa_type: e.target.value})}
                    className="form-input"
                    placeholder="e.g., Canada PR Visa"
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Rating</label>
                  <select
                    value={formData.rating}
                    onChange={(e) => setFormData({...formData, rating: parseInt(e.target.value)})}
                    className="form-input"
                  >
                    {[5,4,3,2,1].map(r => (
                      <option key={r} value={r}>{r} Stars</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Testimonial Text</label>
                <textarea
                  value={formData.text}
                  onChange={(e) => setFormData({...formData, text: e.target.value})}
                  className="form-input"
                  rows="4"
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Date</label>
                  <input
                    type="text"
                    value={formData.date}
                    onChange={(e) => setFormData({...formData, date: e.target.value})}
                    className="form-input"
                    placeholder="e.g., March 2024"
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Display Order</label>
                  <input
                    type="number"
                    value={formData.display_order}
                    onChange={(e) => setFormData({...formData, display_order: parseInt(e.target.value)})}
                    className="form-input"
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                  />
                  <span>Active (visible on website)</span>
                </label>
              </div>

              <div className="modal-actions">
                <button type="button" onClick={closeModal} className="btn-secondary">
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  {editingTestimonial ? 'Update' : 'Create'} Testimonial
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TestimonialsManager;